// Console shell for every product route. The shell is exactly one viewport tall
// (h-screen / 100dvh) and only <main> scrolls, so the header and the
// staleness strip stay pinned on every product page. Composes the header
// (product crumb + global search + status + profile), a product-aware sidebar
// (collapsible on desktop, remembered in localStorage; an accessible
// off-canvas dialog on phones), the product switcher, the ⌘K command palette,
// the shared feed-health strip and the toast outlet. The active product is
// derived from the route, so switching is stateless.
//
// Global keys: ⌘K search · ⌘1–⌘7 switch product (where the browser lets a page
// claim them) · ⌥D toggle table density (with a toast).
//
// Phones (usePhone — below `sm`, or a coarse pointer on a short screen): a
// bottom product tab bar is fixed to the screen edge (mobile browser chrome
// changes the viewport height, so a flow sibling drifted to the end of the page)
// and <main> is padded by its height so content never hides beneath it; the staleness strip collapses to a 28 px line; an
// aria-live region announces feed-state changes. Safe-area insets pad the main
// column (landscape notches) and the bar (home indicator).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { preloadProductHomes } from "@/app/routePreload";
import { Toaster, toast } from "sonner";

import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { Drawer } from "@/app/console/ui";
import { useAuth } from "@/lib/auth/AuthProvider";
import { appForPath, visibleApps, type ConsoleApp } from "@/app/products";
import { ExplainProvider } from "@/app/console/ExplainDrawer";
import { Header } from "@/app/console/Header";
import { Sidebar } from "@/app/console/Sidebar";
import { AppDrawer } from "@/app/console/AppDrawer";
import { CommandPalette } from "@/app/console/CommandPalette";
import { StalenessBanner } from "@/app/console/StalenessBanner";
import { BottomTabBar } from "@/app/console/BottomTabBar";
import { useFeedHealth, type FeedHealth } from "@/app/console/useFeedHealth";
import { applyDensity, toggleDensity, useDensity } from "@/app/console/density";
import { usePhone } from "@/app/console/useViewport";
import { SiteProvider, useSite } from "@/app/site/SiteContext";
import { SiteDrawer } from "@/app/site/SiteDrawer";
import { SiteList, SiteNavRow } from "@/app/site/SiteSwitcher";

const SIDEBAR_KEY = "pb.sidebar.collapsed";

/** "Tickets · PowerBESS" — the matched nav item's label, else the product name. */
function titleFor(app: ConsoleApp, pathname: string): string {
  let best: { label: string; len: number } | null = null;
  for (const group of app.groups) {
    for (const item of group.items) {
      const match = item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (match && (!best || item.to.length > best.len)) best = { label: item.label, len: item.to.length };
    }
  }
  return `${best?.label ?? app.name} · PowerBESS`;
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  } catch {
    return false;
  }
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { roles } = useAuth();

  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  // The Overview launcher opens with the sidebar collapsed to the rail (the
  // page is the navigation); every other page keeps the remembered preference.
  // Toggling on Overview only affects that visit and is never persisted.
  const isOverview = location.pathname === "/overview";
  const [overviewCollapsed, setOverviewCollapsed] = useState(true);
  useEffect(() => {
    if (isOverview) setOverviewCollapsed(true);
  }, [isOverview]);
  const [appDrawerOpen, setAppDrawerOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  const app = useMemo(() => appForPath(location.pathname), [location.pathname]);

  // Warm every product's page chunk once the shell is idle, so switching
  // products never shows a loading skeleton (see app/routePreload.ts).
  useEffect(() => preloadProductHomes(), []);
  // ⌘1–⌘6 are the products in launcher order; manage apps continue the sequence.
  const switchTargets = useMemo(() => {
    const apps = visibleApps(roles);
    return [...apps.filter((a) => a.kind === "product"), ...apps.filter((a) => a.kind === "manage")];
  }, [roles]);

  // One feed-health poll for the whole shell (header dot + staleness strip).
  const feedHealth = useFeedHealth();
  const phone = usePhone();
  const announcement = useFeedAnnouncement(feedHealth);

  // Density preference → <html data-density> (tokens.css reads it).
  const { density } = useDensity();
  useEffect(() => {
    applyDensity();
  }, [density]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);
  const sidebarCollapsed = isOverview ? overviewCollapsed : collapsed;
  const toggleSidebar = useCallback(() => {
    if (isOverview) setOverviewCollapsed((c) => !c);
    else toggleCollapsed();
  }, [isOverview, toggleCollapsed]);

  // Global keyboard: ⌘K palette · ⌘1–9 product switch · ⌥D density.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCommandOpen((o) => !o);
        return;
      }
      if (mod && !e.altKey && !e.shiftKey && /^[1-9]$/.test(e.key)) {
        const target = switchTargets[Number(e.key) - 1];
        if (target) {
          e.preventDefault();
          setAppDrawerOpen(false);
          navigate(target.home);
        }
        return;
      }
      // ⌥D — macOS emits "∂" for Option-D, so match the physical key.
      if (e.altKey && !mod && !e.shiftKey && (e.code === "KeyD" || e.key.toLowerCase() === "d")) {
        const el = e.target as HTMLElement | null;
        if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
        e.preventDefault();
        const next = toggleDensity();
        toast(`Density: ${next}`, {
          description: next === "compact" ? "36 px table rows · ⌥D to switch back" : "44 px table rows · ⌥D to switch back",
          id: "density",
          duration: 2200,
        });
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, switchTargets]);

  // Close transient overlays, reset the scroll position (the window never
  // scrolls — <main> does) and set the tab title on navigation.
  useEffect(() => {
    setMobileNavOpen(false);
    setAppDrawerOpen(false);
    mainRef.current?.scrollTo({ top: 0 });
    document.title = titleFor(app, location.pathname);
  }, [app, location.pathname]);

  const openSwitcher = useCallback(() => {
    setMobileNavOpen(false);
    setAppDrawerOpen(true);
  }, []);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  return (
    <ExplainProvider>
      <SiteProvider>
      <div className="flex h-screen h-dvh overflow-hidden bg-surface-0 text-text-1">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-control focus:bg-brand focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-black"
        >
          Skip to main content
        </a>

        {/* Desktop sidebar — full height, never scrolls with the page */}
        <aside
          aria-label="Application sidebar"
          className={[
            "hidden h-full shrink-0 border-r border-line transition-[width] duration-base ease-pb lg:block",
            sidebarCollapsed ? "w-rail" : "w-sidebar",
          ].join(" ")}
        >
          <Sidebar app={app} collapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} onSwitchProduct={openSwitcher} />
        </aside>

        {/* Phone off-canvas sidebar — a real modal dialog */}
        <MobileNav open={mobileNavOpen} onClose={closeMobileNav} app={app} onSwitchProduct={openSwitcher} />

        {/* Main column: header + strip pinned, <main> scrolls, tab bar below on phones */}
        <div className="flex h-full min-w-0 flex-1 flex-col" style={{ paddingLeft: "var(--pb-safe-left)", paddingRight: "var(--pb-safe-right)" }}>
          <Header
            app={app}
            feedHealth={feedHealth}
            onOpenAppDrawer={() => setAppDrawerOpen(true)}
            onOpenSearch={() => setCommandOpen(true)}
            onOpenMobileNav={() => setMobileNavOpen(true)}
          />
          {/* Thin amber strip under the header. Renders nothing when every feed is healthy. */}
          <StalenessBanner health={feedHealth} />
          {/* Feed-state changes read out by screen readers (the phone strip is a plain button). */}
          <div role="status" aria-live="polite" className="sr-only">
            {announcement}
          </div>
          <main
            ref={mainRef}
            id="main-content"
            className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-4 py-5 sm:px-gutter sm:py-6"
            style={phone ? { paddingBottom: "calc(var(--pb-tabbar-h) + var(--pb-safe-bottom) + 20px)" } : undefined}
          >
            {/* Page template: 1760 px max, centred on ≥1920 screens, full-bleed below.
                The per-page entrance lives inside each route's Suspense boundary
                (App.tsx LazyRoute → PageTransition) so the fade is tied to real
                content, not the loading skeleton. */}
            <div className="mx-auto w-full 3xl:max-w-content">
              <RouteErrorBoundary>
                <Outlet />
              </RouteErrorBoundary>
            </div>
          </main>
          {phone && <BottomTabBar />}
        </div>

        <AppDrawer open={appDrawerOpen} onClose={() => setAppDrawerOpen(false)} activeAppId={app.id} />
        <CommandPalette open={commandOpen} onClose={() => setCommandOpen(false)} />
        {/* Add / edit site drawer + the phone site-picker sheet — one instance for every entry point. */}
        <SiteOverlays />
        <Toaster
          position="bottom-right"
          theme="dark"
          className="pb-toaster"
          closeButton
          mobileOffset={{ bottom: "calc(var(--pb-tabbar-h) + var(--pb-safe-bottom) + 12px)", left: 12, right: 12 }}
        />
      </div>
      </SiteProvider>
    </ExplainProvider>
  );
}

// ---------------------------------------------------------------------------
// Site overlays — the add / edit drawer and, on phones, the site picker as a
// bottom sheet (the header popover is hidden below `sm`). Both read the
// SiteContext, so they must sit inside <SiteProvider>.
// ---------------------------------------------------------------------------

function SiteOverlays() {
  const { pickerOpen, closePicker } = useSite();
  return (
    <>
      <SiteDrawer />
      <Drawer open={pickerOpen} onClose={closePicker} title="Site" description="Choose the site the console shows." width="sm">
        <div className="-mx-1">
          <SiteList onDone={closePicker} autoFocus={false} />
        </div>
      </Drawer>
    </>
  );
}

// ---------------------------------------------------------------------------
// Feed-state announcements — one short sentence whenever the shared feed
// health changes state ("3 feeds need attention", "All feeds live"), read by
// the aria-live region in the shell. Silent while the first poll is pending.
// ---------------------------------------------------------------------------

function useFeedAnnouncement(health: FeedHealth): string {
  const [text, setText] = useState("");
  const lastRef = useRef<string | null>(null);
  useEffect(() => {
    if (health.status === "unknown") return;
    const n = health.unhealthy.length;
    const next =
      health.status === "live"
        ? "All data feeds live"
        : `${n} data feed${n === 1 ? "" : "s"} need attention: ${health.offline.length} offline, ${health.stale.length} stale`;
    if (lastRef.current === null) {
      // First resolved poll: remember, but only announce a problem.
      lastRef.current = next;
      if (health.status !== "live") setText(next);
      return;
    }
    if (next !== lastRef.current) {
      lastRef.current = next;
      setText(next);
    }
  }, [health.status, health.unhealthy.length, health.offline.length, health.stale.length]);
  return text;
}

// ---------------------------------------------------------------------------
// MobileNav — the phone off-canvas sidebar as a modal dialog: Escape closes,
// Tab is trapped inside, focus lands on the first nav item and returns to the
// hamburger on close.
// ---------------------------------------------------------------------------

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function MobileNav({
  open,
  onClose,
  app,
  onSwitchProduct,
}: {
  open: boolean;
  onClose: () => void;
  app: ConsoleApp;
  onSwitchProduct: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const { openPicker } = useSite();

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const focusables = () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const id = window.setTimeout(() => {
      const first = focusables().find((el) => el.closest("nav")) ?? focusables()[0] ?? panelRef.current;
      first?.focus();
    }, 20);
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) return;
      const first = els[0]!;
      const last = els[els.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panelRef.current?.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panelRef.current?.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(id);
      restoreRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-sidebar lg:hidden">
      <button type="button" aria-label="Close navigation" className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" onClick={onClose} tabIndex={-1} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${app.name} navigation`}
        tabIndex={-1}
        className="absolute left-0 top-0 h-full w-[280px] max-w-[85vw] border-r border-line-strong shadow-[0_24px_64px_rgba(0,0,0,0.55)] outline-none"
      >
        <Sidebar
          app={app}
          collapsed={false}
          onToggleCollapse={onClose}
          onSwitchProduct={onSwitchProduct}
          onClose={onClose}
          topSlot={
            <SiteNavRow
              onOpen={() => {
                onClose();
                openPicker();
              }}
            />
          }
        />
      </div>
    </div>
  );
}
